/**
 * Offerings data for specialized SEO landing pages.
 * Each offering targets specific file type keywords.
 */

const offerings = {
  image: {
    slug: 'image-to-url',
    name: 'Image to URL',
    fileType: 'Images',
    tagline: 'Image to URL Converter',
    heroTitle: 'Image to URL',
    heroSubtitle: 'Upload your Images and Get Public URLs Instantly',
    description: 'Get instant public URLs for your images. Convert any format into a direct CDN-backed link for your automation.',
    meta: {
      title: 'Image to URL - Get Public URL for Images Instantly',
      description: 'Need a public URL for an image? Upload your images and get direct CDN-backed links instantly. Perfect for n8n, Zapier, and web development.',
      keywords: 'image to url, get url for image, convert image to link, public image url, image hosting for automation, direct image link generator'
    },
    hero: {
      problem: 'How do you get a direct public URL for an image?',
      solution: 'Whether you\'re building an automation in n8n, sending a Slack message, or embedding an image in a website, you need a reliable, direct URL. Upload your image here and get a CDN-powered link in seconds. No login walls, no redirect pages.'
    },
    features: [
      { title: 'All Formats Supported', description: 'Works with JPG, PNG, GIF, JFIF, AVIF, WebP, SVG, HEIF, and more. If it\'s an image, we can host it.' },
      { title: 'Optimized for Speed', description: 'Images are served through a global CDN, ensuring they load instantly for users worldwide.' },
      { title: 'Direct Deep Links', description: 'Get a raw file URL that works in <img> tags and automation nodes without any modifications.' }
    ],
    faq: [
      { 
        q: 'What image formats are supported?', 
        a: 'We support all common image formats including JPG, PNG, GIF, JFIF, AVIF, WebP, SVG, HEIF, ICO, TIFF, and BMP.' 
      },
      { 
        q: 'Can I use these image URLs in my website?', 
        a: 'Yes! The URLs we provide are direct links that can be used in your HTML <img> tags, CSS files, or any web framework.' 
      },
      { 
        q: 'Is there an image size limit?', 
        a: 'Free accounts can upload images up to 5MB. Business accounts support up to 100MB per image (per upload).' 
      }
    ]
  },
  video: {
    slug: 'video-to-url',
    name: 'Video to URL',
    fileType: 'Videos',
    tagline: 'Video to URL Converter',
    heroTitle: 'Video to URL',
    heroSubtitle: 'Upload your Videos and Get Public URLs Instantly',
    description: 'Host your videos and get direct, permanent links. Ideal for automation workflows, email marketing, and web embeds.',
    meta: {
      title: 'Video to URL - Get Public URL for Videos Instantly',
      description: 'Upload your videos and get direct, CDN-backed public links. Perfect for n8n, Zapier, and sharing videos without login requirements.',
      keywords: 'video to url, get url for video, convert video to link, public video url, video hosting for automation, direct video link generator'
    },
    hero: {
      problem: 'Need a direct, shareable URL for a video file?',
      solution: 'Standard cloud storage links often open in a player page. Our Video to URL tool gives you a direct link to the raw video file. Use it in <video> tags, automation nodes, or share it directly for instant playback.'
    },
    features: [
      { title: 'MP4, MOV, WebM, SWF', description: 'Full support for all major video containers, codecs, and flash files used in web applications.' },
      { title: 'Streaming Ready', description: 'Our CDN supports byte-range requests, allowing for smooth seeking and streaming of your video files.' },
      { title: 'No Buffering Walls', description: 'High-bandwidth delivery ensures your videos start playing instantly across all devices.' }
    ],
    faq: [
      { 
        q: 'What video formats are supported?', 
        a: 'We support MP4, MOV, WebM, AVI, MKV, SWF, and many other video formats.' 
      },
      { 
        q: 'Can I embed these videos on my site?', 
        a: 'Absolutely. Use the direct URL in a standard HTML5 <video> tag or any video player component.' 
      },
      { 
        q: 'What is the maximum video size?', 
        a: 'The max file size depends on your plan, ranging from 5MB (Free) to 100MB (Business) per upload. For larger videos, contact our support.' 
      }
    ]
  },
  pdf: {
    slug: 'pdf-to-url',
    name: 'PDF to URL',
    fileType: 'PDFs',
    tagline: 'PDF to URL Converter',
    heroTitle: 'PDF to URL',
    heroSubtitle: 'Upload your PDFs and Get Public URLs Instantly',
    description: 'Convert your PDF documents into shareable URLs. Perfect for document automation, digital menus, and business reporting.',
    meta: {
      title: 'PDF to URL - Get Public URL for PDFs Instantly',
      description: 'Upload your PDF documents and get direct public links. Ideal for n8n workflows, email attachments, and document sharing.',
      keywords: 'pdf to url, get url for pdf, convert pdf to link, public pdf url, pdf hosting for automation, direct pdf link generator'
    },
    hero: {
      problem: 'Looking for a permanent public link for your PDF documents?',
      solution: 'Stop sending bulky email attachments. Upload your PDF and get a permanent link you can share with anyone. Perfect for invoices, reports, and menus. Works seamlessly with n8n and Make.'
    },
    features: [
      { title: 'Permanent Document Links', description: 'Unlike temporary file sharing sites, our PDF links are permanent and high-availability.' },
      { title: 'Automation Friendly', description: 'Easily trigger PDF uploads from your CRM or accounting software and get back a URL instantly.' },
      { title: 'Direct Browser Viewing', description: 'Links open directly in the browser\'s native PDF viewer for a seamless user experience.' }
    ],
    faq: [
      { 
        q: 'How long do the PDF links last?', 
        a: 'On paid plans, your PDF links can be set to never expire. Free tier links last for 7 days.' 
      },
      { 
        q: 'Can I password protect my PDFs?', 
        a: 'While our links are public, we recommend encrypting the PDF itself if it contains sensitive information.' 
      },
      { 
        q: 'Does it work with n8n?', 
        a: 'Yes, it works perfectly with n8n. You can automatically upload generated PDFs and get a URL to store in your database.' 
      },
      {
        q: 'What is the maximum PDF size?',
        a: 'You can upload PDFs up to 5MB on the Free plan and up to 100MB on the Business plan (per upload).'
      }
    ]
  },
  audio: {
    slug: 'audio-to-url',
    name: 'Audio to URL',
    fileType: 'Audio Files',
    tagline: 'Audio to URL Converter',
    heroTitle: 'Audio to URL',
    heroSubtitle: 'Upload your Audio and Get Public URLs Instantly',
    description: 'Get instant public URLs for your audio files. Convert MP3, WAV, OGA, OGG, OPUS, M4A, MPGA, or AAC into a direct CDN-backed link for your automation.',
    meta: {
      title: 'Audio to URL - Get Public URL for Audio Instantly',
      description: 'Upload your audio files and get direct, CDN-backed public links. Perfect for n8n, Zapier, and sharing audio without login requirements.',
      keywords: 'audio to url, get url for audio, convert audio to link, public audio url, audio hosting for automation, direct audio link generator'
    },
    hero: {
      problem: 'Need a direct, shareable URL for an audio file?',
      solution: 'Most storage services wrap audio in a player page. Audio to URL provides a direct link to the raw audio file, perfect for <audio> tags, podcast feeds, or automation workflows.'
    },
    features: [
      { title: 'MP3, WAV, OGA & more', description: 'Supports all professional and web-ready audio formats for seamless integration.' },
      { title: 'Streaming Optimized', description: 'Our CDN handles range requests, allowing users to scrub and seek through audio files instantly.' },
      { title: 'Crystal Clear Delivery', description: 'High-availability infrastructure ensures your audio plays perfectly every time, everywhere.' }
    ],
    faq: [
      { 
        q: 'What audio formats are supported?', 
        a: 'We support MP3, WAV, OGA, AAC, OGG, FLAC, OPUS, M4A, and MPGA.' 
      },
      { 
        q: 'Can I use these for podcasts?', 
        a: 'Yes! The direct URLs are perfect for RSS feeds or embedding in custom audio players.' 
      },
      { 
        q: 'Is there a limit on audio length?', 
        a: 'We limit by file size (up to 100MB per upload), not duration. Most standard-length tracks fit easily.' 
      }
    ]
  },
  html: {
    slug: 'html-to-url',
    name: 'HTML to URL',
    fileType: 'HTML Files',
    tagline: 'HTML to URL Converter & Live Web Page Hosting',
    heroTitle: 'HTML to URL',
    heroSubtitle: 'Upload your HTML Files and Get Live Public URLs Instantly',
    description: 'Get instant live public URLs for your HTML files. Host single-page websites, email templates, landing pages, and interactive reports with direct CDN links.',
    meta: {
      title: 'HTML to URL - Host HTML Files & Get Public URLs Instantly',
      description: 'Need a public URL for an HTML file or single-page site? Upload your HTML and get instant, CDN-backed live links. Perfect for automations, n8n, previewing email templates, and web development.',
      keywords: 'html to url, host html file, get url for html, convert html to link, live html link generator, publish html file, html file hosting for automation, direct html preview link'
    },
    hero: {
      problem: 'Need to instantly host an HTML file and get a live public URL?',
      solution: 'Whether you\'re rendering automated reports in n8n, sharing an interactive dashboard, testing responsive email templates, or hosting a lightweight landing page, you need a live HTTPS link. Upload your HTML file here and get a fast CDN-backed URL in seconds — served with correct MIME types for direct browser rendering.'
    },
    features: [
      { title: 'Direct Browser Rendering', description: 'HTML files are served with the proper text/html Content-Type headers, rendering directly as live web pages in any browser.' },
      { title: 'Global Edge CDN Delivery', description: 'Your HTML pages and web templates are distributed globally via high-speed edge CDN for instant load times.' },
      { title: 'Automation & API Ready', description: 'Automate HTML report generation from n8n, Zapier, Python, or curl and get back shareable URLs instantly.' },
      { title: 'Inline Styles & Scripts', description: 'Supports modern HTML5 with embedded CSS, JavaScript, SVGs, and web fonts out of the box.' }
    ],
    faq: [
      { 
        q: 'How does HTML to URL work?', 
        a: 'Upload any .html or .htm file, and we instantly store it on our secure CDN and return a direct public HTTPS URL that renders live in any browser without redirect walls.' 
      },
      { 
        q: 'Does it render directly in the browser or force a download?', 
        a: 'It renders directly in the browser! Our CDN serves HTML files with inline content disposition and text/html headers so visitors see your web page immediately.' 
      },
      { 
        q: 'Can I upload HTML generated from n8n, Zapier, or Make workflows?', 
        a: 'Yes! You can use our official n8n community node, Zapier app, or REST API to push dynamically generated HTML files and store the public URL in your database or CRM.' 
      },
      { 
        q: 'Can I use custom domains for my HTML pages?', 
        a: 'Yes, Pro and Business accounts can configure custom domains (e.g., links.yourdomain.com) to brand all generated HTML URLs.' 
      },
      { 
        q: 'What is the maximum file size for HTML uploads?', 
        a: 'Free accounts support HTML files up to 5MB, while paid plans support up to 100MB per file.' 
      }
    ]
  },
  vcf: {
    slug: 'vcf-to-url',
    name: 'VCF to URL',
    fileType: 'VCards',
    tagline: 'VCF to URL Converter',
    heroTitle: 'VCF to URL',
    heroSubtitle: 'Upload your VCF files and Get Public URLs Instantly',
    description: 'Get instant public URLs for your VCF files. Convert VCards into a direct CDN-backed link for your automation.',
    meta: {
      title: 'VCF to URL - Get Public URL for VCards Instantly',
      description: 'Upload your VCF files and get direct public links. Ideal for n8n workflows, email attachments, and contact sharing.',
      keywords: 'vcf to url, get url for vcf, convert vcf to link, public vcf url, vcf hosting for automation, direct vcf link generator'
    },
    hero: {
      problem: 'Need a direct, shareable URL for a VCF file?',
      solution: 'Most storage services wrap VCards in a viewer page. VCF to URL provides a direct link to the raw VCF file, perfect for sharing contacts or automation workflows.'
    },
    features: [
      { title: 'Share Contacts Anywhere', description: 'Supports standard VCF/vCard formats for seamless integration with mobile devices and email clients.' },
      { title: 'Automation Ready', description: 'Easily trigger VCF uploads from your CRM and get back a URL instantly.' },
      { title: 'Lightning Fast', description: 'High-availability infrastructure ensures your VCF downloads perfectly every time, everywhere.' }
    ],
    faq: [
      { 
        q: 'What contact formats are supported?', 
        a: 'We support standard VCF (vCard) files.' 
      },
      { 
        q: 'Can I use these for email signatures?', 
        a: 'Yes! The direct URLs are perfect for linking to your vCard in email signatures.' 
      },
      { 
        q: 'Is there a limit on file size?', 
        a: 'We limit by file size (up to 5MB on the Free plan). Standard VCF files fit easily within this limit.' 
      }
    ]
  },
  hex: {
    slug: 'hex-to-url',
    name: 'HEX to URL',
    fileType: 'HEX Files',
    tagline: 'HEX to URL Converter',
    heroTitle: 'HEX to URL',
    heroSubtitle: 'Upload your HEX files and Get Public URLs Instantly',
    description: 'Get instant public URLs for your HEX files. Convert HEX files into a direct CDN-backed link for your automation.',
    meta: {
      title: 'HEX to URL - Get Public URL for HEX Files Instantly',
      description: 'Upload your HEX files and get direct public links. Ideal for n8n workflows, firmware distribution, and file sharing.',
      keywords: 'hex to url, get url for hex, convert hex to link, public hex url, hex hosting for automation, direct hex link generator'
    },
    hero: {
      problem: 'Need a direct, shareable URL for a HEX file?',
      solution: 'Upload your HEX files and get a direct link to the raw HEX file, perfect for sharing firmware or automation workflows.'
    },
    features: [
      { title: 'Share Firmware Anywhere', description: 'Supports standard HEX formats for seamless integration with IoT devices and embedded systems.' },
      { title: 'Automation Ready', description: 'Easily trigger HEX uploads from your CI/CD pipeline and get back a URL instantly.' },
      { title: 'Lightning Fast', description: 'High-availability infrastructure ensures your HEX file downloads perfectly every time, everywhere.' }
    ],
    faq: [
      { 
        q: 'What formats are supported?', 
        a: 'We support standard HEX files.' 
      },
      { 
        q: 'Can I use these for OTA updates?', 
        a: 'Yes! The direct URLs are perfect for Over-The-Air (OTA) firmware updates for IoT devices.' 
      },
      { 
        q: 'Is there a limit on file size?', 
        a: 'We limit by file size (up to 5MB on the Free plan). Standard HEX files fit easily within this limit.' 
      }
    ]
  },
  srt: {
    slug: 'srt-to-url',
    name: 'SRT to URL',
    fileType: 'SRT Subtitles',
    tagline: 'SRT to URL Converter',
    heroTitle: 'SRT to URL',
    heroSubtitle: 'Upload your SRT subtitles and Get Public URLs Instantly',
    description: 'Get instant public URLs for your SRT files. Convert subtitles into a direct CDN-backed link for your video players.',
    meta: {
      title: 'SRT to URL - Get Public URL for Subtitles Instantly',
      description: 'Upload your SRT subtitles and get direct public links. Ideal for n8n workflows, video players, and subtitle sharing.',
      keywords: 'srt to url, get url for srt, convert srt to link, public srt url, subtitle hosting, direct srt link generator'
    },
    hero: {
      problem: 'Need a direct, shareable URL for an SRT subtitle file?',
      solution: 'Upload your SRT files and get a direct link to the raw subtitle file, perfect for video players and automation workflows.'
    },
    features: [
      { title: 'Share Subtitles Anywhere', description: 'Supports standard SRT formats for seamless integration with web video players.' },
      { title: 'Automation Ready', description: 'Easily trigger SRT uploads from your video processing pipeline and get back a URL instantly.' },
      { title: 'Lightning Fast', description: 'High-availability infrastructure ensures your subtitles load perfectly every time, everywhere.' }
    ],
    faq: [
      { 
        q: 'What formats are supported?', 
        a: 'We support standard SRT subtitle files.' 
      },
      { 
        q: 'Can I use these in HTML5 video players?', 
        a: 'Yes! The direct URLs are perfect for <track> elements in HTML5 video.' 
      },
      { 
        q: 'Is there a limit on file size?', 
        a: 'We limit by file size (up to 5MB on the Free plan). Subtitle files fit easily within this limit.' 
      }
    ]
  },
  vtt: {
    slug: 'vtt-to-url',
    name: 'VTT to URL',
    fileType: 'VTT Subtitles',
    tagline: 'VTT to URL Converter',
    heroTitle: 'VTT to URL',
    heroSubtitle: 'Upload your VTT subtitles and Get Public URLs Instantly',
    description: 'Get instant public URLs for your VTT files. Convert WebVTT subtitles into a direct CDN-backed link for your video players.',
    meta: {
      title: 'VTT to URL - Get Public URL for WebVTT Instantly',
      description: 'Upload your VTT subtitles and get direct public links. Ideal for n8n workflows, web video players, and subtitle sharing.',
      keywords: 'vtt to url, get url for vtt, convert vtt to link, public vtt url, webvtt hosting, direct vtt link generator'
    },
    hero: {
      problem: 'Need a direct, shareable URL for a WebVTT file?',
      solution: 'Upload your VTT files and get a direct link to the raw WebVTT file, perfect for HTML5 video players and automation workflows.'
    },
    features: [
      { title: 'Share Subtitles Anywhere', description: 'Supports standard WebVTT formats for seamless integration with HTML5 video players.' },
      { title: 'Automation Ready', description: 'Easily trigger VTT uploads from your video processing pipeline and get back a URL instantly.' },
      { title: 'Lightning Fast', description: 'High-availability infrastructure ensures your subtitles load perfectly every time, everywhere.' }
    ],
    faq: [
      { 
        q: 'What formats are supported?', 
        a: 'We support standard WebVTT (VTT) subtitle files.' 
      },
      { 
        q: 'Can I use these in HTML5 video players?', 
        a: 'Yes! The direct URLs are perfect for <track> elements in HTML5 video.' 
      },
      { 
        q: 'Is there a limit on file size?', 
        a: 'We limit by file size (up to 5MB on the Free plan). Subtitle files fit easily within this limit.' 
      }
    ]
  },
  ttf: {
    slug: 'ttf-to-url',
    name: 'TTF to URL',
    fileType: 'TrueType Fonts',
    tagline: 'TTF to URL Converter',
    heroTitle: 'TTF to URL',
    heroSubtitle: 'Upload your TTF fonts and Get Public URLs Instantly',
    description: 'Get instant public URLs for your TTF files. Convert TrueType fonts into a direct CDN-backed link for your web projects.',
    meta: {
      title: 'TTF to URL - Get Public URL for Fonts Instantly',
      description: 'Upload your TTF fonts and get direct public links. Ideal for web design, app development, and font sharing.',
      keywords: 'ttf to url, get url for ttf, convert ttf to link, public ttf url, font hosting, direct ttf link generator'
    },
    hero: {
      problem: 'Need a direct, shareable URL for a TTF font file?',
      solution: 'Upload your TTF files and get a direct link to the raw TrueType font file, perfect for @font-face in CSS and web development.'
    },
    features: [
      { title: 'Share Fonts Anywhere', description: 'Supports standard TTF formats for seamless integration with web browsers and CSS.' },
      { title: 'Development Ready', description: 'Easily link your fonts directly into your stylesheets using @font-face.' },
      { title: 'Lightning Fast', description: 'High-availability infrastructure ensures your fonts load perfectly every time, everywhere.' }
    ],
    faq: [
      { 
        q: 'What formats are supported?', 
        a: 'We support standard TTF (TrueType Font) files.' 
      },
      { 
        q: 'Can I use these in CSS?', 
        a: 'Yes! The direct URLs are perfect for @font-face declarations in your stylesheets.' 
      },
      { 
        q: 'Is there a limit on file size?', 
        a: 'We limit by file size (up to 5MB on the Free plan). Font files fit easily within this limit.' 
      }
    ]
  }
};

export default offerings;

/**
 * Lookup map from URL slug to offering key.
 */
export const slugToOfferingKey = Object.fromEntries(
  Object.entries(offerings).map(([key, data]) => [data.slug, key])
);

export const offeringList = Object.values(offerings);
